import React, { useState, useEffect, useRef } from 'react';
import { STATUS_OPTIONS, FONT } from '../constants';
import { C } from '../styles';
import type { StatusKey } from '../types';

interface Props {
  value: StatusKey[];
  onApply: (next: StatusKey[]) => void;
}

export default function FilterPopover({ value, onApply }: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<StatusKey[]>(value || []);
  const [search, setSearch] = useState<string>('');
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = (key: StatusKey): void => {
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : s.concat([key])));
  };
  const remove = (key: StatusKey): void => {
    setSelected((s) => s.filter((k) => k !== key));
  };
  const clear = (): void => setSelected([]);
  const apply = (): void => {
    onApply(selected);
    setOpen(false);
  };

  const filtered = STATUS_OPTIONS.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px',
          background: selected.length > 0 ? C.brandBg : '#fff',
          border: '1px solid ' + (selected.length > 0 ? C.brand : C.line),
          borderRadius: 8, fontSize: 13,
          color: selected.length > 0 ? C.brandDark : C.ink,
          fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 2 }}>
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        Filters{selected.length > 0 ? ' (' + selected.length + ')' : ''} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 42, left: 0,
          background: '#fff', border: '1px solid ' + C.line,
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          width: 300, zIndex: 30,
        }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + C.line }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>Filter by</div>
          </div>
          <div style={{ padding: '14px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Status
            </div>
            <div style={{
              border: '1px solid ' + C.line, borderRadius: 8,
              padding: '8px 10px', marginBottom: 10,
              minHeight: 38, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
            }}>
              {selected.map((k) => {
                const opt = STATUS_OPTIONS.find((o) => o.key === k);
                if (!opt) return null;
                return (
                  <span key={k} style={{
                    background: C.brandBg, color: C.brandDarkText,
                    fontSize: 11, fontWeight: 600,
                    padding: '3px 8px', borderRadius: 4,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}>
                    {opt.label}
                    <span onClick={() => remove(k)} style={{ cursor: 'pointer', opacity: 0.6 }}>×</span>
                  </span>
                );
              })}
              <input
                type="text"
                placeholder={selected.length === 0 ? 'Search status...' : ''}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  border: 'none', outline: 'none',
                  fontSize: 12, color: C.muted,
                  flex: 1, minWidth: 80, padding: '2px 4px',
                  fontFamily: FONT,
                }}
              />
            </div>
            <div style={{ border: '1px solid ' + C.line, borderRadius: 8, overflow: 'hidden' }}>
              {filtered.map((o, idx) => {
                const isSel = selected.includes(o.key);
                return (
                  <div
                    key={o.key}
                    onClick={() => toggle(o.key)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', fontSize: 13, color: C.ink, cursor: 'pointer',
                      borderTop: idx === 0 ? 'none' : '1px solid ' + C.line,
                      background: isSel ? C.page : '#fff',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: isSel ? C.brand : 'transparent', fontWeight: 700 }}>✓</span>
                      <span>{o.label}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{
            padding: '12px 18px', borderTop: '1px solid ' + C.line,
            display: 'flex', justifyContent: 'space-between', gap: 10,
          }}>
            <button
              onClick={clear}
              style={{
                flex: 1, background: 'transparent',
                border: '1px solid ' + C.line, color: C.ink,
                fontSize: 13, fontWeight: 600,
                padding: '8px 14px', borderRadius: 8,
                cursor: 'pointer', fontFamily: FONT,
              }}
            >
              Clear all
            </button>
            <button
              onClick={apply}
              style={{
                flex: 1, background: C.brand, color: 'white',
                fontSize: 13, fontWeight: 600,
                padding: '8px 14px', borderRadius: 8,
                border: 'none', cursor: 'pointer', fontFamily: FONT,
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}